/**
 * Fetch all pages from a paginated DRF endpoint.
 * Follows pagination links up to maxPages, collecting results.
 * @param {Function} baseQuery - RTK baseQuery function
 * @param {string} url - Base URL to paginate
 * @param {Object} params - Additional params to pass
 * @param {Object} options - { pageSize: number (max 500 server-side), maxPages: number }
 * @returns {Promise<{data: Array, meta?: {truncated: boolean}} | {error: *}>}
 */
export async function fetchAllPages(
  baseQuery,
  url,
  params = {},
  { pageSize = 500, maxPages = 20 } = {}
) {
  const rows = [];
  let page = 1;

  while (page <= maxPages) {
    const result = await baseQuery({
      url,
      params: { ...params, page, page_size: pageSize },
    });

    if (result.error) {
      return { error: result.error };
    }

    const body = result.data;

    // Handle unpaginated array responses
    if (Array.isArray(body)) {
      return { data: body };
    }

    // Collect results from paginated response
    rows.push(...(body?.results ?? []));

    // Stop if no next page
    if (!body?.next) {
      return { data: rows };
    }

    page += 1;
  }

  // Reached maxPages limit while more data exists
  return { data: rows, meta: { truncated: true } };
}
